const KNOWN_LABELS = {
  basicRent: 'Base Rent',
  storage: 'Storage',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const formatCurrency = (value) => {
  const n = parseFloat(value);
  if (value === null || value === undefined || isNaN(n)) return '$0.00';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const humanize = (key) =>
  String(key)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const labelFor = (row, key) =>
  row[`${key}_label`] || KNOWN_LABELS[key] || humanize(key);

const toDateParts = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
};

export const formatLeaseRange = (lease) => {
  if (!lease) return null;
  const s = toDateParts(lease.start);
  const e = toDateParts(lease.end);
  const fmt = (p) => `${MONTHS[p.m - 1]} ${p.d}, ${p.y}`;
  if (s && e) {
    if (s.y === e.y) return `${MONTHS[s.m - 1]} ${s.d} – ${MONTHS[e.m - 1]} ${e.d}, ${e.y}`;
    return `${fmt(s)} – ${fmt(e)}`;
  }
  if (s) return fmt(s);
  if (e) return fmt(e);
  return null;
};

export const buildUnitChargeSummary = (row, feeColumns = []) => {
  const empty = { lines: [], total: 0, deposit: null, lease: null };
  if (!row || typeof row !== 'object') return empty;

  const lines = [];
  const pushNumeric = (key, label) => {
    const amount = parseFloat(row[key]);
    if (!isNaN(amount) && amount > 0) lines.push({ key, label, amount });
  };

  // 1. Base Rent
  pushNumeric('basicRent', labelFor(row, 'basicRent'));

  // 2. Other flat fee columns (skip object-typed keys handled below)
  (feeColumns || []).forEach((key) => {
    if (key === 'basicRent' || key === 'parking' || key === 'storage') return;
    pushNumeric(key, labelFor(row, key));
  });

  // 3. Parking — surface + underground as separate lines
  const parking = row.parking;
  if (parking && typeof parking === 'object') {
    const surface = parseFloat(parking.surfaceStallFee);
    const underground = parseFloat(parking.undergroundFee);
    if (!isNaN(surface) && surface > 0) {
      lines.push({ key: 'parking.surface', label: 'Surface Parking', amount: surface });
    }
    if (!isNaN(underground) && underground > 0) {
      lines.push({ key: 'parking.underground', label: 'Underground Parking', amount: underground });
    }
  }

  // 4. Storage
  const storage = row.storage;
  if (storage != null) {
    const fee = typeof storage === 'object' ? parseFloat(storage.fee) : parseFloat(storage);
    if (!isNaN(fee) && fee > 0) {
      lines.push({ key: 'storage', label: labelFor(row, 'storage'), amount: fee });
    }
  }

  const total = lines.reduce((sum, l) => sum + l.amount, 0);

  // Deposit (excluded from total)
  let deposit = null;
  const sd = row.securityDeposit != null ? row.securityDeposit : row.sd;
  if (sd != null) {
    if (typeof sd === 'object') {
      const sum = (parseFloat(sd.leaseDeposit) || 0) + (parseFloat(sd.petDeposit) || 0);
      deposit = sum > 0 ? sum : null;
    } else {
      const n = parseFloat(sd);
      deposit = isNaN(n) || n <= 0 ? null : n;
    }
  }

  // Lease range
  let lease = null;
  const ld = row.leaseDetails;
  if (ld && (ld.leaseStartDMY || ld.leaseEndDMY)) {
    lease = { start: ld.leaseStartDMY || null, end: ld.leaseEndDMY || null };
  }

  return { lines, total, deposit, lease };
};
